import React, { useState, useEffect, useRef } from 'react';
import { saveAgreement } from '../services/api';
import '../stylesheet/FranchiseAgreement.css';
import axios from 'axios';

const AgreementForm = () => {
  const [legalTerms, setLegalTerms] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistChecks, setChecklistChecks] = useState({});

  // Centralized form state tracking all inputs across the 5-step wizard
  const [form, setForm] = useState({
    agreement_date: '',
    form_type: '',
    franchisee_name: '',
    age: '',
    email: '',
    address: '',
    location: '',
    agreement_period: '',
    agreement_start_date: '',
    agreement_end_date: '',
    franchisee_amount: '',
    franchisee_amount_words: '',
    cheque_number: '',
    cheque_date: '',
    bank_name: '',
    sharing_recruitment: '',
    instant_no_replace: '',
    portal_type: '',
    portal_years: '',
    portal_hours: '',
    portal_view: '',
    portal_posting: '',
    portal_mails: '',
    franchisee_title: '',
    witness1: '',
    witness2: '',
    receipt_name: '',
    receipt_amount: '',
    receipt_amount_words: '',
    receipt_cheque: '',
    receipt_date: '',
    receipt_bank: '',
    receipt_final_amount: '',
    receipt_witness1: '',
    receipt_witness2: '',
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [signatureFile, setSignatureFile] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ---------- FETCH LEGAL TERMS & DYNAMIC CHECKLIST ----------
  useEffect(() => {
    axios.get('https://www.gurukrupaeducation.com/api/get_agreement_terms.php')
      .then(res => setLegalTerms(res.data))
      .catch(err => console.error('Failed to load terms', err));

    axios.get('https://www.gurukrupaeducation.com/api/get_checklist.php')
      .then(res => {
        setChecklistItems(res.data);
        const initialChecks = {};
        res.data.forEach(item => { initialChecks[item.id] = false; });
        setChecklistChecks(initialChecks);
      })
      .catch(err => console.error('Failed to load checklist', err));
  }, []);

  // ---------- AUTO-FILL RECEIPT LOGIC ----------
  // This hook watches specific source fields. Whenever a user types into them, 
  // their values are mirrored onto the matching receipt state fields automatically.
  useEffect(() => {
    setForm(prev => ({
      ...prev, // Safely copy all existing form key-value pairs
      
      receipt_name: prev.franchisee_name,                 // Maps Full Name from Step 1
      receipt_amount: prev.franchisee_amount,             // Maps Numeric Fee from Step 3
      receipt_amount_words: prev.franchisee_amount_words, // Maps Wording Fee from Step 3
      receipt_cheque: prev.cheque_number,                 // Maps Instrument Ref No from Step 3
      receipt_date: prev.cheque_date,                     // Maps Transaction Date from Step 3
      receipt_bank: prev.bank_name,                       // Maps Bank Origin from Step 3
      receipt_final_amount: prev.franchisee_amount,       // Maps Confirmed Ledger Clearance Value
      
      // Optional: Pre-populates receipt witnesses with the primary document witnesses
      receipt_witness1: prev.witness1,                    // Maps Witness 1 from Step 4
      receipt_witness2: prev.witness2,                    // Maps Witness 2 from Step 4
    }));
  }, [
    // Dependency array: Tells React exactly which inputs to keep an eye on.
    // Code inside this useEffect runs only when one of these variables updates.
    form.franchisee_name, 
    form.franchisee_amount, 
    form.franchisee_amount_words, 
    form.cheque_number, 
    form.cheque_date, 
    form.bank_name,
    form.witness1,
    form.witness2
  ]);


  // ---------- CANVAS DRAWING ----------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resizeCanvas = () => {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = 120;
    };
    resizeCanvas();

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e) => {
      e.preventDefault();
      isDrawing.current = true;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const draw = (e) => {
      if (!isDrawing.current) return;
      e.preventDefault();
      const pos = getPos(e);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#000000';
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    };

    const stopDrawing = () => {
      if (!isDrawing.current) return;
      isDrawing.current = false;
      canvasToFile();
    };

    canvas.addEventListener('mousedown', startDrawing);
    canvas.addEventListener('mousemove', draw);
    canvas.addEventListener('mouseup', stopDrawing);
    canvas.addEventListener('mouseleave', stopDrawing);
    canvas.addEventListener('touchstart', startDrawing, { passive: false });
    canvas.addEventListener('touchmove', draw, { passive: false });
    canvas.addEventListener('touchend', stopDrawing);

    return () => {
      if (canvas) {
        canvas.removeEventListener('mousedown', startDrawing);
        canvas.removeEventListener('mousemove', draw);
        canvas.removeEventListener('mouseup', stopDrawing);
      }
    };
  }, [currentStep]);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureFile(null);
  };

  const canvasToFile = () => {
    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'signature.png', { type: 'image/png' });
        setSignatureFile(file);
      }
    }, 'image/png');
  };

  // ---------- INPUT HANDLERS ----------
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  const toggleChecklistItem = (id) => {
    setChecklistChecks(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const nextStep = () => {
    const cur = currentStep;
    if (cur === 0) {
      const req = ['agreement_date', 'form_type', 'franchisee_name', 'age', 'email', 'address', 'location', 'agreement_period', 'agreement_start_date', 'agreement_end_date'];
      for (let f of req) if (!form[f].toString().trim()) { alert('Fill all required fields'); return; }
    } else if (cur === 2) {
      const req = ['franchisee_amount', 'franchisee_amount_words', 'cheque_number', 'cheque_date', 'bank_name', 'sharing_recruitment', 'instant_no_replace', 'portal_type', 'portal_years', 'portal_hours', 'portal_view', 'portal_posting', 'portal_mails'];
      for (let f of req) if (!form[f].toString().trim()) { alert('Fill all required fields'); return; }
    } else if (cur === 3) {
      const req = ['franchisee_title', 'witness1', 'witness2'];
      for (let f of req) if (!form[f].toString().trim()) { alert('Fill all required fields'); return; }
      const allChecked = checklistItems.every(item => checklistChecks[item.id] === true);
      if (!allChecked) {
        alert('Please tick all checklist items to confirm your agreement');
        return;
      }
    }
    goToStep(cur + 1);
  };

  const prevStep = () => goToStep(currentStep - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const req5 = ['receipt_name', 'receipt_amount', 'receipt_amount_words', 'receipt_cheque', 'receipt_date', 'receipt_bank', 'receipt_final_amount', 'receipt_witness1', 'receipt_witness2'];
    for (let f of req5) if (!form[f].toString().trim()) { alert('Fill all receipt fields'); return; }
    if (!signatureFile) { alert('Draw your signature'); return; }
    if (!photoFile) { alert('Upload a photo'); return; }
    
    setSubmitting(true);
    const data = new FormData();
    Object.keys(form).forEach(key => {
      const value = form[key];
      data.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : value);
    });
    
    const checklistArray = checklistItems.map(item => ({
      id: item.id,
      accepted: checklistChecks[item.id] ? true : false,
    }));
    data.append('checklist', JSON.stringify(checklistArray));
    data.append('signature', signatureFile, 'signature.png');
    data.append('photo', photoFile, photoFile.name);

    try {
      const res = await saveAgreement(data);
      setMessage('Agreement submitted completely! Reference ID: ' + res.data.id);
    } catch (err) {
      console.error(err);
      alert('Submission failed');
    }
    setSubmitting(false);
  };

  const steps = ['Parties Details', 'Agreement Clauses', 'Schedules & Fees', 'Execution & Sign', 'Receipt'];

  return (
    <div className="wrap">
      <form onSubmit={handleSubmit}>
        
        {/* Stepper tracking header */}
        <div className="stepper">
          {steps.map((label, idx) => (
            <div key={idx} className={`step-item${idx === currentStep ? ' active' : ''}${idx < currentStep ? ' done' : ''}`}
                 onClick={() => idx < currentStep && goToStep(idx)}>
              <div className="step-num">{idx < currentStep ? '✓' : idx + 1}</div>
              <div className="step-label">{label}</div>
            </div>
          ))}
        </div>

        {/* ==================== THE LEGAL PAPER PAGE BLOCK ==================== */}
        <div className="legal-paper">
          
          {/* Main Simulated Document Letterhead Header */}
          <div className="stamp-header-simulation">
            <h1>GuruKrupa Placement &amp; Education Solutions Pvt Ltd</h1>
            <div className="sub-tag">(An International Brand for Adhyatmic, Social and Profit Earning)</div>
            <div className="reg-uin">Registration UIN No. U72900MH2021PTC371525</div>
          </div>

          <div className="document-title">Service Agreement</div>

          {/* ===== STEP 0 – PARTIES DETAILS ===== */}
          <div className={`section${currentStep === 0 ? ' active' : ''}`}>
            <p className="legal-clause">
              THIS FRANCHISE AGREEMENT is made and entered into at Mumbai on this date
              <input type="date" name="agreement_date" value={form.agreement_date} onChange={handleInputChange} style={{ width: '160px' }} required />,
              <strong> BY AND BETWEEN:</strong>
            </p>

            <p className="legal-clause" style={{ paddingLeft: '30px' }}>
              <strong>M/s. GURUKRUPA PLACEMENT &amp; EDUCATION SOLUTIONS PVT LTD</strong>, a Company duly incorporated and registered under the provisions of the Companies Act 2013, having Corporate Identification Number UIN U72900MH2021PTC371525, having its business office at Shop no 21, Plot No 62, Ghrakul CHS LTD, Gorai No 1, Near Swami Vivekananda School, Borivali West, Mumbai - 400092, Maharashtra, India (Hereinafter called <strong>"THE FRANCHISOR"</strong>).
            </p>

            <p className="legal-clause"><strong>AND</strong></p>

            <p className="legal-clause" style={{ paddingLeft: '30px' }}>
              <strong>Ms/Mr/Smt/Miss:</strong>
              <input type="text" name="franchisee_name" value={form.franchisee_name} onChange={handleInputChange} placeholder="Full Legal Name" style={{ width: '280px' }} required />,
              age: <input type="number" name="age" value={form.age} onChange={handleInputChange} placeholder="Years" style={{ width: '70px' }} min="18" required />,
              Email: <input type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="your@email.com" style={{ width: '220px' }} required />,
              residing at: <input type="text" name="address" value={form.address} onChange={handleInputChange} placeholder="Complete Residential Address" style={{ width: '90%', marginTop: '8px' }} required />,
              located at <input type="text" name="location" value={form.location} onChange={handleInputChange} placeholder="City, State" style={{ width: '180px' }} required />, India (Hereinafter called <strong>"THE FRANCHISEE"</strong>).
            </p>

            <div className="legal-clause-title">WHEREAS:</div>
            <ol type="a" style={{ paddingLeft: '40px' }}>
              <li>The Franchisor has been carrying on the business of placement in the name and style of "M/s. GURUKRUPA PLACEMENT &amp; EDUCATION SOLUTIONS PVT LTD".</li>
              <li>The Franchisor is the registered owner of the trademark and design of the company.</li>
              <li>Pursuant to the discussion between the parties, the Franchisor wishes to appoint the Franchisee of the business of Placement in India.</li>
            </ol>

            <div className="legal-grid-row">
              <div className="legal-field-group">
                <label>Select Agreement Package Layer</label>
                <select name="form_type" value={form.form_type} onChange={handleInputChange} required>
                  <option value="" disabled>Select package tier</option>
                  <option value="100_lifetime">100% — Lifetime</option>
                  <option value="100_3years">100% — 3 Years</option>
                  <option value="75_2years">75% — 2 Years</option>
                  <option value="50_1year">50% — 1 Year</option>
                </select>
              </div>
              <div className="legal-field-group">
                <label>Agreement Period Text</label>
                <input type="text" name="agreement_period" value={form.agreement_period} onChange={handleInputChange} placeholder="e.g. 3 years" required />
              </div>
            </div>

            <div className="legal-grid-row">
              <div className="legal-field-group">
                <label>Commencement Date</label>
                <input type="date" name="agreement_start_date" value={form.agreement_start_date} onChange={handleInputChange} required />
              </div>
              <div className="legal-field-group">
                <label>Termination Date</label>
                <input type="date" name="agreement_end_date" value={form.agreement_end_date} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="navbar">
              <span style={{ fontSize: '13px', fontStyle: 'italic' }}>Page 1 of 5</span>
              <button type="button" className="btn btn-n" onClick={nextStep}>Next: Terms Clauses →</button>
            </div>
          </div>

          {/* ===== STEP 1 – AGREEMENT CLAUSES ===== */}
          <div className={`section${currentStep === 1 ? ' active' : ''}`}>
            <div className="legal-clause-title">IT IS HEREBY AGREED AND RECORDED AS FOLLOWS:</div>
            
            <div style={{ maxHeight: '450px', overflowY: 'auto', paddingRight: '10px', fontSize: '15px' }}>
              <p>1. The FRANCHISOR hereby appoints the FRANCHISEE for the business of HR SOLUTIONS in the name and style of "M/s. GURUKRUPA PLACEMENT &amp; EDUCATION SOLUTIONS PVT. LTD.".</p>
              <p>2. The operational period of this agreement shall run for the selected duration explicitly stated on Page 1 from the start date to the expiry terms unless terminated under provisions defined.</p>
              <p>3. Job portal controls are structured outside direct software ownership of the FRANCHISOR; uptime matches third-party service-level terms perfectly.</p>
              <p>4. Intellectual property, trade branding parameters, structural marks, and digital assets remain explicitly locked under the ownership layers of the FRANCHISOR exclusively.</p>
              
              {legalTerms.map((t) => (
                <div key={t.id || t.clause_number} style={{ marginBottom: '12px' }}>
                  <strong>{t.clause_number}.</strong> <span dangerouslySetInnerHTML={{ __html: t.clause_text }} />
                </div>
              ))}
            </div>

            <div className="navbar">
              <button type="button" className="btn btn-p" onClick={prevStep}>← Back</button>
              <span style={{ fontSize: '13px', fontStyle: 'italic' }}>Page 2 of 5</span>
              <button type="button" className="btn btn-n" onClick={nextStep}>Next: Ratios &amp; Fees →</button>
            </div>
          </div>

          {/* ===== STEP 2 – SCHEDULES & FEES ===== */}
          <div className={`section${currentStep === 2 ? ' active' : ''}`}>
            <div className="legal-clause-title">SCHEDULE 'A': COMPENSATION &amp; SHARING SCHEDULE</div>
            
            <p className="legal-clause">
              The FRANCHISEE has paid to the FRANCHISOR a lump sum FRANCHISEE consideration fee of Rs.
              <input type="text" name="franchisee_amount" value={form.franchisee_amount} onChange={handleInputChange} placeholder="e.g. 50000" style={{ width: '90px' }} required />
              (Rupees <input type="text" name="franchisee_amount_words" value={form.franchisee_amount_words} onChange={handleInputChange} placeholder="Amount in words" style={{ width: '240px' }} required /> Only),
              via Cheque / Ref No. <input type="text" name="cheque_number" value={form.cheque_number} onChange={handleInputChange} placeholder="Instrument No." style={{ width: '120px' }} required />,
              Dated <input type="date" name="cheque_date" value={form.cheque_date} onChange={handleInputChange} required /> 
              drawn on Bank <input type="text" name="bank_name" value={form.bank_name} onChange={handleInputChange} placeholder="Bank Name" style={{ width: '140px' }} required /> as non-refundable structural processing capital.
            </p>

            <div className="legal-clause-title">REVENUE RATIO LAYERS</div>
            <table className="legal-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>Sr</th>
                  <th>Service Particular Classification</th>
                  <th style={{ width: '150px', textAlign: 'center' }}>Payable Ratio Share</th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-section-row"><td colSpan={3}>Recruitment Ratios</td></tr>
                <tr>
                  <td>1</td>
                  <td>Recruitment Solutions (Sourced against any business clients)</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input type="text" name="sharing_recruitment" value={form.sharing_recruitment} onChange={handleInputChange} placeholder="Share" style={{ width: '50px', textAlign: 'center' }} required />%
                    </div>
                  </td>
                </tr>
                
                <tr className="table-section-row"><td colSpan={3}>Other HR Managed Matrices</td></tr>
                <tr><td>2</td><td>Other HR Services –<br></br>
1. Recruitments <br></br>
2. Staffs Training (Complete Training out sources)<br></br>
3. Industrial Training<br></br>
4. Payroll<br></br>
5. Compliance<br></br>

6. Background Verification<br></br>
7. Marksheet Verifications<br></br>
8. Address Verifications<br></br>
9. Tenant Verification<br></br>
10. Police Verifications<br></br>
11. HR Policies<br></br>
12. Management policies<br></br>
13. IR<br></br>
14. PMS<br></br>
15. HR Audit<br></br>
16. Management Consultant<br></br>
17. Complete HR out sources<br></br>
18. College Campus Drives.<br></br>
19. Hire College batches for Campus selection.<br></br>
(Complete Campus placement out sources for College/Institute)<br></br>
20. Jobs Fair<br></br>
21. Candidates for event for temporary time.<br></br>
22. Driver Agency<br></br>
23. Maid Agency<br></br>
24. Nurse Agency<br></br>
25. Housekeeping agency<br></br>
26. Office boys Agency<br></br>
27. Facilities Management<br></br>
28.Contract Staffs<br></br>
29. Security Agency<br></br>
30. HR Courses Teaching</td>
<td></td>
</tr>
                <tr><td>2a</td><td>Client sourced by FRANCHISEE, execution fulfillment by FRANCHISEE</td><td style={{ textAlign: 'center' }}>90%</td></tr>
                <tr><td>2b</td><td>Client sourced by FRANCHISOR, execution fulfillment by FRANCHISEE</td><td style={{ textAlign: 'center' }}>50%</td></tr>
                <tr className="table-section-row"><td colSpan={3}>Instant Payout Enterprise Structures</td></tr>
                <tr>
                  <td>3a</td>
                  <td>Instant settlement terms (No replacement liabilities applied)</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input type="text" name="instant_no_replace" value={form.instant_no_replace} onChange={handleInputChange} placeholder="Share" style={{ width: '50px', textAlign: 'center' }} required />%
                    </div>
                  </td>
                </tr>
                <tr><td>3b</td><td>Instant settlement terms (Fulfillment replacements requested)</td><td style={{ textAlign: 'center' }}>50% Sourced</td></tr>
                <tr>
                  <td>4</td>
                  <td>Recruitment (every month providing Company)</td>
                  <td>50%</td>
                </tr>
               
                  
                  <tr className="table-section-row">
                    <td>5</td><td colSpan={3}>For HR. 100%,100%,75% Application form</td></tr>
                    <tr>
                      <td>a.</td>
                      <td>Apart from Recruitment, If The FRANCHISEE wants to start any other Hr. services or any other business activity such as from Education, Mega mall, Banking product, Real estate, Securities Services etc., and where no additional cost come on Gurukrupa,</td>
                      <td>No need to pay additional amount</td>
                    </tr>

                <tr>
                  <td>b.</td>
                  <td>
                    Apart from Recruitment, If the FRANCHISEE wants to provide any other HR services or any other business activity such as Education, Mega mall, Banking product, Real estate, Securities Services etc., and where additional cost comes on Gurukrupa.

Example – Security agency, Skill India, MSCIT course, School, Medical licence etc., here required additional amount on every new location code.
                  </td>
                  <td>The FRANCHISEE have to pay additional cost</td>
                </tr>
                <tr>
                  <td>
                    6.
                  </td>
                  <td>
                    For other than HR business
                  </td>
                  <td>50%</td>

                </tr>
                <tr>
                  <td>a.</td>
                  <td>For billing upto Rs.10k for 1 year</td>
                  <td>75%</td>
                </tr>
                <tr>
                  <td>b.</td>
                  <td>For billing upto 30k for 1 year</td>
                  <td>100%</td>
                </tr>
              
              </tbody>
            </table>

            <div className="legal-clause-title">JOB PORTAL CONFIGURATION CREDITS</div>
            <div className="legal-grid-row">
              <div className="legal-field-group"><label>Portal Type Tag</label><input type="text" name="portal_type" value={form.portal_type} onChange={handleInputChange} placeholder="e.g. Naukri" required /></div>
              <div className="legal-field-group"><label>Validity (Years)</label><input type="text" name="portal_years" value={form.portal_years} onChange={handleInputChange} placeholder="e.g. 2" required /></div>
              <div className="legal-field-group"><label>Daily Access Hours</label><input type="text" name="portal_hours" value={form.portal_hours} onChange={handleInputChange} placeholder="e.g. 8" required /></div>
            </div>
            <div className="legal-grid-row">
              <div className="legal-field-group"><label>Annual View Credits</label><input type="text" name="portal_view" value={form.portal_view} onChange={handleInputChange} placeholder="Count" required /></div>
              <div className="legal-field-group"><label>Annual Posting Credits</label><input type="text" name="portal_posting" value={form.portal_posting} onChange={handleInputChange} placeholder="Count" required /></div>
              <div className="legal-field-group"><label>Annual Mail Utility</label><input type="text" name="portal_mails" value={form.portal_mails} onChange={handleInputChange} placeholder="Count" required /></div>
            </div>

            <div className="navbar">
              <button type="button" className="btn btn-p" onClick={prevStep}>← Back</button>
              <span style={{ fontSize: '13px', fontStyle: 'italic' }}>Page 3 of 5</span>
              <button type="button" className="btn btn-n" onClick={nextStep}>Next: Sign Verification →</button>
            </div>
          </div>

          {/* ===== STEP 3 – EXECUTION & SIGN ===== */}
          <div className={`section${currentStep === 3 ? ' active' : ''}`}>
            <div className="legal-clause-title">DECREE OF ACCEPTANCE CHECKLIST</div>
            
            <div style={{ marginBottom: '25px' }}>
              {checklistItems.map((item) => (
                <div key={item.id} className="legal-checklist-item" onClick={() => toggleChecklistItem(item.id)}>
                  <input type="checkbox" checked={checklistChecks[item.id] || false} readOnly />
                  <label style={{ cursor: 'pointer' }}><strong>{item.label}</strong></label>
                </div>
              ))}
            </div>

            <div className="signature-grid">
              <div className="signature-block">
                <div className="signature-title">FRANCHISEE — SIGNATORY</div>
                <div className="legal-field-group" style={{ marginBottom: '12px' }}>
                  <label>Full Signatory Name</label>
                  <input type="text" name="franchisee_title" value={form.franchisee_title} onChange={handleInputChange} placeholder="As per Identity ID" required />
                </div>
                
                <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>PASSPORT PHOTO MATCH</label>
                <div className="photo-upload-box" onClick={() => document.getElementById('legal_photo_input').click()}>
                  <span>📷 Click to select passport photo</span>
                  {photoPreview && <img src={photoPreview} alt="Preview Attachment" />}
                </div>
                <input type="file" id="legal_photo_input" style={{ display: 'none' }} accept="image/*" onChange={handlePhotoChange} />

                <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginTop: '12px' }}> SIGN HERE</label>
                <div className="canvas-container">
                  <canvas className="signature-canvas" ref={canvasRef}></canvas>
                </div>
                <button type="button" className="clear-sig-btn" onClick={clearSignature}>Clear Line</button>
              </div>

              <div className="signature-block" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <div className="signature-title">FRANCHISOR — EXECUTANT</div>
                  <p style={{ fontSize: '13px', color: '#555', fontStyle: 'italic' }}>
                    M/s. Gurukrupa Placement &amp; Education Solutions Pvt Ltd Corporate Seal stamp and managing authority verification metrics will update dynamically post background clearance validation.
                  </p>
                </div>
                <div style={{ borderTop: '1px dashed #bbb', paddingTop: '10px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                  PENDING FINAL ADM APPROVAL STAMP
                </div>
              </div>
            </div>

            <div style={{ marginTop: '20px', background: '#f8fafc', padding: '15px', border: '1px solid #e2e8f0' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>INSTRUMENT WITNESSES</label>
              <div className="legal-grid-row" style={{ margin: '0' }}>
                <div className="legal-field-group"><input type="text" name="witness1" value={form.witness1} onChange={handleInputChange} placeholder="Witness 1 Full Name" required /></div>
                <div className="legal-field-group"><input type="text" name="witness2" value={form.witness2} onChange={handleInputChange} placeholder="Witness 2 Full Name" required /></div>
              </div>
            </div>

            <div className="navbar">
              <button type="button" className="btn btn-p" onClick={prevStep}>← Back</button>
              <span style={{ fontSize: '13px', fontStyle: 'italic' }}>Page 4 of 5</span>
              <button type="button" className="btn btn-n" onClick={nextStep}>Next: Issue Receipt →</button>
            </div>
          </div>

          {/* ===== STEP 4 – RECEIPT (Now safely auto-filled!) ===== */}
          <div className={`section${currentStep === 4 ? ' active' : ''}`}>
            <div style={{ border: '2px solid #000', padding: '30px', background: '#fff' }}>
              <div style={{ textALign: 'center', fontWeight: 'bold', fontSize: '20px', textDecoration: 'underline', marginBottom: '20px', textAlign: 'center' }}>
                OFFICIAL COGNIZANCE RECEIPT
              </div>
              
              <p className="legal-clause">
                RECEIVED from the Franchisee Named: Ms/Mr/Smt/Miss 
                <input type="text" name="receipt_name" value={form.receipt_name} onChange={handleInputChange} placeholder="Receiver Identity" style={{ width: '220px' }} required />,
                the formal consideration total of ₹ 
                <input type="text" name="receipt_amount" value={form.receipt_amount} onChange={handleInputChange} placeholder="Value (Numeric)" style={{ width: '90px' }} required /> 
                (Rupees <input type="text" name="receipt_amount_words" value={form.receipt_amount_words} onChange={handleInputChange} placeholder="Value in words description" style={{ width: '260px' }} required /> Only),
                acknowledged as consideration parameters allocated toward execution steps of the Corporate Placement Franchise Agreement structure.
              </p>

              <p className="legal-clause">
                Settled through Transaction / Cheque / UPI / NEFT  Ref No: <input type="text" name="receipt_cheque" value={form.receipt_cheque} onChange={handleInputChange} placeholder="Ref No." style={{ width: '130px' }} required />,
                Dated <input type="date" name="receipt_date" value={form.receipt_date} onChange={handleInputChange} required /> 
                originated from Banking Entity Institution: <input type="text" name="receipt_bank" value={form.receipt_bank} onChange={handleInputChange} placeholder="Bank Name" style={{ width: '160px' }} required />.
              </p>

              <p className="legal-clause" style={{ fontWeight: 'bold', marginTop: '25px' }}>
                I SAY RECEIVED CLEAR VALUE TOTAL VALUE: ₹ <input type="text" name="receipt_final_amount" value={form.receipt_final_amount} onChange={handleInputChange} placeholder="Final Confirmed Amount" style={{ width: '100px' }} required />/-
              </p>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '40px' }}>
                <div style={{ width: '45%' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '10px' }}>RECEIPT WITNESSES SIGNATURES:</div>
                  <input type="text" name="receipt_witness1" value={form.receipt_witness1} onChange={handleInputChange} placeholder="Witness 1 Name" style={{ width: '90%', marginBottom: '8px' }} required />
                  <input type="text" name="receipt_witness2" value={form.receipt_witness2} onChange={handleInputChange} placeholder="Witness 2 Name" style={{ width: '90%' }} required />
                </div>
                <div style={{ width: '45%', textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 'bold', textTransform: 'uppercase' }}>FOR GURUKRUPA PLACEMENT AUTHORIZED DECREE</span>
                  <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>Authorized Signatory Signature Registered Digitally</span>
                </div>
              </div>
            </div>

            <p style={{ color: '#c2410c', fontStyle: 'italic', fontSize: '13px', marginTop: '15px', fontWeight: 'bold' }}>
              * NOTICE: Validation checks require confirmed financial banking verification ledger clear logs for valid contract activations.
            </p>

            <div className="navbar">
              <button type="button" className="btn btn-p" onClick={prevStep}>← Back</button>
              <span style={{ fontSize: '13px', fontStyle: 'italic' }}>Page 5 of 5</span>
              <button type="submit" className="btn btn-s" disabled={submitting}>
                {submitting ? 'Processing Ledger Enrolment...' : 'Execute & Finalize Agreement ✓'}
              </button>
            </div>
          </div>

        </div>
      </form>

      {message && (
        <div style={{ marginTop: '20px', padding: '20px', background: '#dcfce7', border: '1px solid #16a34a', color: '#15803d', textAlign: 'center', fontWeight: 'bold', borderRadius: '4px' }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AgreementForm;